<template>
  <div class="space-y-6">
    <!-- Date Range Selector -->
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">
        Analytics Overview
      </h3>
      <select
        v-model="dateRange"
        @change="fetchStats"
        class="text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg">
        <option value="7">
          Last 7 days
        </option>
        <option value="30">
          Last 30 days
        </option>
        <option value="90">
          Last 90 days
        </option>
      </select>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-8">
      <svg aria-hidden="true" class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary-600" viewBox="0 0 100 101" fill="none">
        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
      </svg>
    </div>

    <!-- No Data -->
    <div v-else-if="!hasGaData && !hasGscData" class="text-center py-8">
      <p class="text-sm text-gray-500 dark:text-gray-400">
        No analytics data yet. Data will appear after the initial sync completes.
      </p>
      <p v-if="syncing" class="text-xs text-primary-600 dark:text-primary-400 mt-2">
        Sync in progress...
      </p>
    </div>

    <template v-else>
      <!-- GA4 Summary Cards -->
      <div v-if="hasGaData">
        <h4 class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-3">
          Google Analytics (GA4)
        </h4>
        <div class="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Sessions
            </p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {{ formatNumber(gaTotals.sessions) }}
            </p>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-green-200 dark:border-green-800 p-4">
            <p class="text-xs text-green-600 dark:text-green-400">
              Organic Sessions
            </p>
            <p class="text-xl font-semibold text-green-700 dark:text-green-300 mt-1">
              {{ formatNumber(gaTotals.organicSessions) }}
            </p>
            <p v-if="gaTotals.sessions > 0" class="text-[10px] text-green-500 dark:text-green-500 mt-0.5">
              {{ formatPercent(gaTotals.organicSessions / gaTotals.sessions) }} of total
            </p>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Users
            </p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {{ formatNumber(gaTotals.totalUsers) }}
            </p>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Page Views
            </p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {{ formatNumber(gaTotals.pageViews) }}
            </p>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Bounce Rate
            </p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {{ formatPercent(gaTotals.bounceRate) }}
            </p>
          </div>
        </div>

        <!-- Channel Breakdown -->
        <div v-if="channelBreakdown.length" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mt-3">
          <h5 class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">
            Sessions by Channel
          </h5>
          <div class="space-y-2">
            <div v-for="ch in channelBreakdown" :key="ch.channel" class="flex items-center gap-3">
              <span class="text-xs text-gray-600 dark:text-gray-300 w-28 shrink-0 truncate">
                {{ ch.label }}
              </span>
              <div class="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  class="h-full rounded-full"
                  :class="ch.barClass"
                  :style="{ width: ch.percent + '%' }" />
              </div>
              <span class="text-xs text-gray-500 dark:text-gray-400 w-14 text-right tabular-nums">
                {{ formatNumber(ch.sessions) }}
              </span>
              <span class="text-[10px] text-gray-400 w-10 text-right tabular-nums">
                {{ ch.percent }}%
              </span>
            </div>
          </div>
        </div>

        <!-- GA4 Daily Chart (total + organic overlay) -->
        <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mt-3">
          <div class="flex items-center justify-between mb-3">
            <h5 class="text-xs font-medium text-gray-500 dark:text-gray-400">
              Sessions by Day
            </h5>
            <div class="flex items-center gap-3 text-[10px]">
              <span class="flex items-center gap-1">
                <span class="w-2 h-2 rounded-sm bg-primary-500" />
                <span class="text-gray-400">Total</span>
              </span>
              <span class="flex items-center gap-1">
                <span class="w-2 h-2 rounded-sm bg-green-500" />
                <span class="text-gray-400">Organic</span>
              </span>
            </div>
          </div>
          <div class="flex items-end space-x-1 h-24">
            <div
              v-for="day in gaDailyChart"
              :key="day.date"
              :title="`${day.date}: ${day.sessions} total, ${day.organic} organic`"
              class="flex-1 h-full relative">
              <div
                class="absolute bottom-0 w-full bg-primary-500 dark:bg-primary-400 rounded-t min-h-[2px]"
                :style="{ height: day.heightPercent + '%' }" />
              <div
                class="absolute bottom-0 w-full bg-green-500 dark:bg-green-400 rounded-t"
                :style="{ height: day.organicHeightPercent + '%' }" />
            </div>
          </div>
          <div class="flex justify-between mt-1">
            <span class="text-[10px] text-gray-400">{{ gaDailyChart[0]?.date }}</span>
            <span class="text-[10px] text-gray-400">{{ gaDailyChart[gaDailyChart.length - 1]?.date }}</span>
          </div>
        </div>
      </div>

      <!-- GSC Summary Cards -->
      <div v-if="hasGscData">
        <h4 class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-3">
          Search Console (GSC)
        </h4>
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Clicks
            </p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {{ formatNumber(gscTotals.clicks) }}
            </p>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Impressions
            </p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {{ formatNumber(gscTotals.impressions) }}
            </p>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Avg CTR
            </p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {{ formatPercent(gscTotals.ctr) }}
            </p>
          </div>
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Avg Position
            </p>
            <p class="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {{ gscTotals.position.toFixed(1) }}
            </p>
          </div>
        </div>

        <!-- GSC Daily Chart -->
        <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mt-3">
          <h5 class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">
            Clicks by Day
          </h5>
          <div class="flex items-end space-x-1 h-24">
            <div
              v-for="day in gscDailyChart"
              :key="day.date"
              :title="`${day.date}: ${day.clicks} clicks`"
              class="flex-1 bg-green-500 dark:bg-green-400 rounded-t min-h-[2px] transition-all hover:bg-green-600"
              :style="{ height: day.heightPercent + '%' }" />
          </div>
          <div class="flex justify-between mt-1">
            <span class="text-[10px] text-gray-400">{{ gscDailyChart[0]?.date }}</span>
            <span class="text-[10px] text-gray-400">{{ gscDailyChart[gscDailyChart.length - 1]?.date }}</span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { getGaStats, getGscStats } from '~/javascript/api/analytics.js'
import { createConsumer } from '@rails/actioncable'

const props = defineProps({
  websiteId: { type: String, required: true },
  gaEnabled: { type: Boolean, default: false },
  gscEnabled: { type: Boolean, default: false }
})

const emit = defineEmits(['date-range-changed'])

const loading = ref(true)
const syncing = ref(false)
const dateRange = ref('30')
const gaData = ref([])
const gscData = ref([])
let subscription = null

const hasGaData = computed(() => gaData.value.length > 0)
const hasGscData = computed(() => gscData.value.length > 0)

const gaTotals = computed(() => {
  const totals = { sessions: 0, organicSessions: 0, totalUsers: 0, pageViews: 0, bounceRate: 0 }
  if (!gaData.value.length) return totals

  gaData.value.forEach((d) => {
    totals.sessions += d.sessions || 0
    totals.totalUsers += d.total_users || 0
    totals.pageViews += d.screen_page_views || 0
    totals.bounceRate += d.bounce_rate || 0
    if (d.channel === 'organic_search') {
      totals.organicSessions += d.sessions || 0
    }
  })
  totals.bounceRate /= gaData.value.length
  return totals
})

const channelBreakdown = computed(() => {
  const byChannel = {}
  gaData.value.forEach((d) => {
    const ch = d.channel || 'unassigned'
    byChannel[ch] = (byChannel[ch] || 0) + (d.sessions || 0)
  })
  const total = Object.values(byChannel).reduce((a, b) => a + b, 0) || 1
  const labels = {
    organic_search: 'Organic Search',
    direct: 'Direct',
    referral: 'Referral',
    organic_social: 'Organic Social',
    paid_search: 'Paid Search',
    organic_video: 'Organic Video',
    email: 'Email',
    display: 'Display',
    paid_social: 'Paid Social',
    unassigned: 'Unassigned'
  }
  const barClasses = {
    organic_search: 'bg-green-500',
    direct: 'bg-primary-500',
    referral: 'bg-purple-500',
    organic_social: 'bg-pink-500',
    paid_search: 'bg-amber-500',
    organic_video: 'bg-red-500',
    email: 'bg-cyan-500'
  }
  return Object.entries(byChannel)
    .map(([channel, sessions]) => ({
      channel,
      label: labels[channel] || channel.replace(/_/g, ' '),
      sessions,
      percent: Math.round((sessions / total) * 100),
      barClass: barClasses[channel] || 'bg-gray-400'
    }))
    .sort((a, b) => b.sessions - a.sessions)
})

const gscTotals = computed(() => {
  const totals = { clicks: 0, impressions: 0, ctr: 0, position: 0 }
  if (!gscData.value.length) return totals

  gscData.value.forEach((d) => {
    totals.clicks += d.total_clicks || 0
    totals.impressions += d.total_impressions || 0
    totals.position += d.position || 0
  })
  totals.ctr = totals.impressions > 0 ? totals.clicks / totals.impressions : 0
  totals.position /= gscData.value.length
  return totals
})

const gaDailyChart = computed(() => {
  const totalByDate = {}
  const organicByDate = {}
  gaData.value.forEach((d) => {
    totalByDate[d.date] = (totalByDate[d.date] || 0) + (d.sessions || 0)
    if (d.channel === 'organic_search') {
      organicByDate[d.date] = (organicByDate[d.date] || 0) + (d.sessions || 0)
    }
  })
  const entries = Object.keys(totalByDate).sort().map((date) => ({
    date,
    sessions: totalByDate[date],
    organic: organicByDate[date] || 0
  }))
  const max = Math.max(...entries.map((e) => e.sessions), 1)
  return entries.map((e) => ({
    ...e,
    heightPercent: (e.sessions / max) * 100,
    organicHeightPercent: (e.organic / max) * 100
  }))
})

const gscDailyChart = computed(() => {
  const entries = gscData.value
    .map((d) => ({ date: d.date, clicks: d.total_clicks || 0 }))
    .sort((a, b) => a.date.localeCompare(b.date))
  const max = Math.max(...entries.map((e) => e.clicks), 1)
  return entries.map((e) => ({ ...e, heightPercent: (e.clicks / max) * 100 }))
})

const formatNumber = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
  return n.toString()
}

const formatPercent = (n) => `${(n * 100).toFixed(1)}%`

const fetchStats = async() => {
  loading.value = true
  const days = parseInt(dateRange.value, 10)
  const startDate = new Date(Date.now() - days * 86400000).toISOString().split('T')[0]
  const endDate = new Date().toISOString().split('T')[0]
  const params = { start_date: startDate, end_date: endDate }
  emit('date-range-changed', { startDate, endDate })

  try {
    const [gaResp, gscResp] = await Promise.all([
      props.gaEnabled ? getGaStats(props.websiteId, params) : Promise.resolve({ data: []}),
      props.gscEnabled ? getGscStats(props.websiteId, params) : Promise.resolve({ data: []})
    ])
    gaData.value = gaResp.data || []
    gscData.value = gscResp.data || []
  } catch (error) {
    console.error('Error fetching analytics stats:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchStats()

  // Emit initial date range
  const days = parseInt(dateRange.value, 10)
  const initStartDate = new Date(Date.now() - days * 86400000).toISOString().split('T')[0]
  const initEndDate = new Date().toISOString().split('T')[0]
  emit('date-range-changed', { startDate: initStartDate, endDate: initEndDate })

  const consumer = createConsumer()
  subscription = consumer.subscriptions.create(
    { channel: 'AnalyticsSyncChannel', website_id: props.websiteId },
    {
      received(data) {
        if (data.status === 'complete') {
          syncing.value = false
          fetchStats()
        }
      }
    }
  )
})

onUnmounted(() => {
  if (subscription) subscription.unsubscribe()
})
</script>
